// Tab "Phiếu đối soát": danh sách phiếu của tôi + chi tiết tới từng đơn (Phụ lục B), in / lưu PDF
// bằng hộp in của trình duyệt. Chỉ đọc — ShopTrack lập, duyệt và chuyển khoản.

import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Printer } from 'lucide-react'
import { get } from '@/lib/api'
import { formatDate, formatDateTime, money, toBankingUnaccent } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useViewportMode } from '@/hooks/useViewportMode'
import { Button } from '@/components/ui/button'
import { LoadError } from '@/components/ui/data-notice'
import { Sheet } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import DataTable, { INDEX_CELL, INDEX_HEADER, IndexBadge, type DataTableColumn } from '@/components/ui/data-table'
import { SignedAmount } from '@/components/orders/order-money'
import { VoucherStatusBadge } from './parts'
import {
  KIND_LABEL,
  VOUCHER_BUCKETS,
  VOUCHER_KIND_LABEL,
  VOUCHER_STATUS,
  bucketOf,
  outcomeLabel,
  sortedKinds,
  type Voucher,
  type VoucherBuckets,
  type VoucherDetail,
} from './types'

const PRINT_CLASS = 'printing-voucher'

function maskAccount(n: string | null): string {
  if (!n) return ''
  return n.length > 4 ? `···${n.slice(-4)}` : n
}

export function ReconcileVouchers() {
  // Phiếu đang mở nằm trên URL (?tab=vouchers&voucher=PDS-…) — gửi link là mở đúng phiếu.
  const [params, setParams] = useSearchParams()
  const openCode = params.get('voucher')
  const setOpenCode = (code: string | null) => setParams(code ? { tab: 'vouchers', voucher: code } : { tab: 'vouchers' }, { replace: true })
  const q = useQuery({ queryKey: ['reconcile', 'vouchers'], queryFn: () => get<{ vouchers: Voucher[] }>('/reconcile/vouchers') })
  const vouchers = q.data?.vouchers ?? []

  const columns: DataTableColumn<Voucher>[] = [
    {
      key: 'code',
      header: 'Mã phiếu',
      priority: 'primary',
      cell: (v) => (
        <span className="block">
          <span className="block font-mono text-sm font-medium text-ink">{v.code}</span>
          <span className="block text-xs text-muted">{VOUCHER_KIND_LABEL[v.kind] ?? v.kind}</span>
        </span>
      ),
    },
    {
      key: 'period',
      header: 'Kỳ',
      priority: 'secondary',
      cell: (v) => <span className="whitespace-nowrap text-inkSoft">Đến {formatDate(v.cutoffAt)}</span>,
    },
    { key: 'orders', header: 'Số đơn', align: 'right', cell: (v) => <span className="tabular-nums text-ink">{v.orderCount.toLocaleString('vi-VN')}</span> },
    { key: 'net', header: 'Số tiền', align: 'right', cell: (v) => <SignedAmount value={v.netAmount} debtTag={false} className="font-semibold" /> },
    { key: 'status', header: 'Trạng thái', cell: (v) => <VoucherStatusBadge status={v.status} /> },
    {
      key: 'paid',
      header: 'Ngày chi',
      cell: (v) => <span className="whitespace-nowrap tabular-nums text-inkSoft">{v.paidAt ? formatDateTime(v.paidAt) : '—'}</span>,
    },
    {
      key: 'bank',
      header: 'Tài khoản nhận',
      cell: (v) =>
        v.kind === 'offset' ? (
          <span className="text-xs text-muted">Cấn trừ — không chuyển khoản</span>
        ) : v.bankName || v.bankAccountNumber ? (
          <span className="whitespace-nowrap text-inkSoft">
            {v.bankName} <span className="font-mono">{maskAccount(v.bankAccountNumber)}</span>
          </span>
        ) : (
          <span className="text-xs text-muted">—</span>
        ),
    },
  ]

  return (
    <section className="space-y-3">
      <div className="overflow-hidden rounded-xl bg-raise shadow-card">
        {q.isError ? (
          <LoadError onRetry={() => q.refetch()}>Không tải được phiếu đối soát.</LoadError>
        ) : (
          <DataTable
            columns={columns}
            data={vouchers}
            rowKey={(v) => v.id}
            loading={q.isLoading}
            onRowClick={(v) => setOpenCode(v.code)}
            emptyLabel="Chưa có phiếu đối soát nào."
          />
        )}
      </div>

      <Sheet
        open={!!openCode}
        onOpenChange={(o) => !o && setOpenCode(null)}
        title={openCode ? `Phiếu ${openCode}` : 'Phiếu đối soát'}
        className="sm:max-w-3xl"
      >
        {openCode && <VoucherDetailPanel code={openCode} />}
      </Sheet>
    </section>
  )
}

function VoucherDetailPanel({ code }: { code: string }) {
  const q = useQuery({
    queryKey: ['reconcile', 'voucher', code],
    queryFn: () => get<{ voucher: VoucherDetail }>(`/reconcile/vouchers/${encodeURIComponent(code)}`),
  })

  // Rời ngăn kéo giữa chừng → trả lại trang in bình thường.
  useEffect(() => () => document.body.classList.remove(PRINT_CLASS), [])

  if (q.isError) return <LoadError onRetry={() => q.refetch()}>Không tải được phiếu {code}.</LoadError>
  const v = q.data?.voucher
  if (q.isLoading || !v) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  function print() {
    const prevTitle = document.title
    const done = () => {
      document.body.classList.remove(PRINT_CLASS)
      document.title = prevTitle
      window.removeEventListener('afterprint', done)
    }
    document.title = v!.code // tên tệp gợi ý khi "Lưu thành PDF"
    document.body.classList.add(PRINT_CLASS)
    window.addEventListener('afterprint', done)
    window.print()
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <VoucherStatusBadge status={v.status} />
        <Button variant="outline" onClick={print} className="gap-1.5">
          <Printer className="size-4" /> In / Tải PDF
        </Button>
      </div>
      <VoucherDocument v={v} />
      {createPortal(
        <div className="voucher-print-root" aria-hidden>
          <VoucherDocument v={v} print />
        </div>,
        document.body,
      )}
    </div>
  )
}

/** Nội dung phiếu — dùng chung cho màn hình và bản in. */
function VoucherDocument({ v, print = false }: { v: VoucherDetail; print?: boolean }) {
  // Điện thoại (không phải bản in): "Từng đơn" thành danh sách thẻ — bảng 5 cột phải kéo ngang
  const cards = useViewportMode() === 'mobile' && !print
  const isOffset = v.kind === 'offset'
  // Đếm số đơn có khoản rơi vào từng nhóm (giống cột "38 đơn" ở Phụ lục B).
  const orderCount: Partial<Record<keyof VoucherBuckets, number>> = {}
  for (const o of v.orders) {
    if (!o.orderId && !o.waybillCode) continue
    const seen = new Set<keyof VoucherBuckets>()
    for (const l of o.lines) seen.add(bucketOf(l.kind, l.amount))
    for (const b of seen) orderCount[b] = (orderCount[b] ?? 0) + 1
  }
  const buckets = VOUCHER_BUCKETS.filter((b) => v[b.key] !== 0)
  const orders = v.orders.filter((o) => o.orderId || o.waybillCode)
  const others = v.orders.filter((o) => !o.orderId && !o.waybillCode)

  return (
    <div className={cn('space-y-5 text-sm', print && 'text-[12px]')}>
      <div className={cn('space-y-1.5', !print && 'rounded-xl bg-paperSoft p-4')}>
        {print && (
          <div className="flex items-baseline justify-between gap-4 border-b pb-2">
            <p className="text-lg font-bold">PHIẾU ĐỐI SOÁT {v.code}</p>
            <p>Trạng thái: {VOUCHER_STATUS[v.status]?.text ?? v.status}</p>
          </div>
        )}
        <InfoRow label="Loại phiếu">{VOUCHER_KIND_LABEL[v.kind] ?? v.kind}</InfoRow>
        <InfoRow label="Người nhận">{v.payeeName ?? v.payeeKey}</InfoRow>
        <InfoRow label="Kỳ">Đến {formatDateTime(v.cutoffAt)}</InfoRow>
        {!isOffset && (
          <InfoRow label="Tài khoản">
            {v.bankAccountNumber ? (
              <>
                {v.bankName} <span className="font-mono">{v.bankAccountNumber}</span>
                {v.bankAccountHolder ? ` — ${v.bankAccountHolder}` : ''}
              </>
            ) : (
              'Chưa có tài khoản nhận'
            )}
          </InfoRow>
        )}
      </div>

      <table className="w-full border-collapse">
        <tbody>
          {buckets.map((b, i) => (
            <tr key={b.key} className="border-b border-rule align-top">
              <td className="py-2 pr-3 text-muted tabular-nums">{i + 1}.</td>
              <td className="py-2 pr-3">
                <span className="block text-ink">{b.label}</span>
                {b.hint && <span className="block text-xs text-muted">{b.hint}</span>}
              </td>
              <td className="whitespace-nowrap py-2 pr-3 text-right text-inkSoft tabular-nums">{orderCount[b.key] ? `${orderCount[b.key]!.toLocaleString('vi-VN')} đơn` : ''}</td>
              <td className={cn('whitespace-nowrap py-2 text-right font-mono tabular-nums', v[b.key] < 0 ? 'text-danger' : 'text-ink')}>{money(v[b.key])}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-ink/70">
            <td />
            <td className="py-2.5 font-semibold text-ink" colSpan={2}>
              {isOffset ? 'SỐ RÒNG (CẤN TRỪ — KHÔNG CHUYỂN KHOẢN)' : 'THỰC NHẬN'}
            </td>
            <td className="whitespace-nowrap py-2.5 text-right font-mono text-base font-bold tabular-nums text-ink">{money(v.netAmount)}</td>
          </tr>
        </tbody>
      </table>

      <div className="space-y-1 text-xs text-inkSoft">
        {!isOffset && (
          <p>
            Nội dung chuyển khoản: <span className="font-mono text-ink">{toBankingUnaccent(v.code)}</span>
          </p>
        )}
        <p>
          Lập: {formatDateTime(v.createdAt)}
          {v.approvedAt ? ` · Duyệt: ${formatDateTime(v.approvedAt)}` : ''}
          {v.paidAt ? ` · Chi: ${formatDateTime(v.paidAt)}` : ''}
          {v.bankRef ? ` (${v.bankRef})` : ''}
        </p>
        {v.status === 'cancelled' && <p className="text-warn">Phiếu đã hủy{v.cancelReason ? `: ${v.cancelReason}` : ''} — các khoản trở về "Sẵn sàng chi" và vào phiếu sau.</p>}
      </div>

      {orders.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-ink">Từng đơn ({orders.length.toLocaleString('vi-VN')})</h3>
          {cards ? (
            <ul className="divide-y divide-rule rounded-lg border border-rule text-xs">
              {orders.map((o, i) => (
                <li key={o.financeId ?? i} className="space-y-1.5 px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="flex min-w-0 items-baseline gap-2">
                      <IndexBadge n={i + 1} />
                      <span className="min-w-0 break-all font-mono text-ink">{o.waybillCode ?? '—'}</span>
                    </span>
                    <span className={cn('shrink-0 font-mono font-semibold tabular-nums', o.total < 0 ? 'text-danger' : 'text-ink')}>{money(o.total)}</span>
                  </div>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-inkSoft">
                    <span>
                      {outcomeLabel(o.status, o.returnStage)}
                      {o.orderId && <span className="text-muted"> · {o.orderId}</span>}
                    </span>
                    <span>
                      COD thu <span className="font-mono tabular-nums text-ink">{o.cod == null ? '—' : money(o.cod)}</span>
                    </span>
                  </div>
                  <LinesByKind lines={o.lines} />
                </li>
              ))}
            </ul>
          ) : (
            <div className={cn('overflow-x-auto', !print && 'rounded-lg border border-rule')}>
              <table className="w-full text-left text-xs">
                <thead className="border-b border-rule text-inkSoft">
                  <tr>
                    <th className={cn('px-2.5 py-2 font-medium', INDEX_CELL)}>{INDEX_HEADER}</th>
                    <th className="px-2.5 py-2 font-medium">Mã vận đơn</th>
                    <th className="px-2.5 py-2 font-medium">Kết quả</th>
                    <th className="px-2.5 py-2 text-right font-medium">COD thu</th>
                    <th className="px-2.5 py-2 font-medium">Các khoản</th>
                    <th className="px-2.5 py-2 text-right font-medium">Tổng</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rule">
                  {orders.map((o, i) => (
                    <tr key={o.financeId ?? i} className="align-top">
                      <td className={cn('px-2.5 py-2', INDEX_CELL)}>{i + 1}</td>
                      <td className="px-2.5 py-2">
                        <span className="block font-mono text-ink">{o.waybillCode ?? '—'}</span>
                        {o.orderId && <span className="block text-muted">{o.orderId}</span>}
                      </td>
                      <td className="px-2.5 py-2 text-inkSoft">{outcomeLabel(o.status, o.returnStage)}</td>
                      <td className="whitespace-nowrap px-2.5 py-2 text-right font-mono tabular-nums text-ink">{o.cod == null ? '—' : money(o.cod)}</td>
                      <td className="px-2.5 py-2">
                        <LinesByKind lines={o.lines} />
                      </td>
                      <td className={cn('whitespace-nowrap px-2.5 py-2 text-right font-mono font-semibold tabular-nums', o.total < 0 ? 'text-danger' : 'text-ink')}>{money(o.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {others.length > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-ink">Khoản không theo đơn</h3>
          <ul className="space-y-1 text-xs">
            {others.map((o, i) => (
              <li key={i} className="flex flex-wrap items-baseline justify-between gap-2">
                <LinesByKind lines={o.lines} />
                <span className={cn('font-mono font-semibold tabular-nums', o.total < 0 ? 'text-danger' : 'text-ink')}>{money(o.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function LinesByKind({ lines }: { lines: { kind: string; amount: number }[] }) {
  const byKind: Record<string, number> = {}
  for (const l of lines) byKind[l.kind] = (byKind[l.kind] ?? 0) + l.amount
  return (
    <span className="flex flex-col gap-0.5">
      {sortedKinds(byKind).map(([k, a]) => (
        <span key={k} className="whitespace-nowrap text-inkSoft">
          {KIND_LABEL[k] ?? 'Khoản khác'}{' '}
          <span className={cn('font-mono tabular-nums', a < 0 ? 'text-danger' : 'text-ink')}>
            {a > 0 ? '+' : ''}
            {money(a)}
          </span>
        </span>
      ))}
    </span>
  )
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p className="flex flex-wrap gap-x-3">
      <span className="w-24 shrink-0 text-muted">{label}</span>
      <span className="min-w-0 text-ink">{children}</span>
    </p>
  )
}
